/* eslint-disable promise/param-names */
import { connect, nuid, StringCodec, AckPolicy, DeliverPolicy, Events, DebugEvents } from 'nats.ws'
import { setBusy, clearBusy } from 'irmlib/containers'
import { LOCAL_AI_ASSET_IMG } from '../../irmlib/src/common'
import { firestore, doc, getDoc } from '../../utils/firestore'
import * as constants from '../../irmlib/src/common/constants'

const GDE_SETTINGS = 'gdeSettings'
const WEBHOOK = 'scLocalQCWebHook'

class NatsService {
  constructor() {
    this.connection = null
    this.serverURL = null
    this.isConnecting = false
    this.connectionPromise = null
    this.retryLimitReached = false
    this.disposed = false
    this.isReconnecting = false
    this.connectionRestoreSucces = false
    // for now binding state update function directly
    this.onConnectionStateChange = () => {}
  }

  registerStateCallback(stateUpdateFunction) {
    this.onConnectionStateChange = stateUpdateFunction
  }

  async monitorNats() {
    // console.log('iterator is mounted')
    if (!this.connection) {
      return
    }
    // this will stay in the loop and automatically if terminated when connection ended
    for await (const status of this.connection.status()) {
      // addtional check in the loop if connection already terminated
      if (this.disposed) break
      switch (status.type) {
        case Events.Disconnect:
          console.warn('NATS disconnected from:', status?.data)
          this.isReconnecting = true
          this.onConnectionStateChange({
            isConnected: false,
            isReconnecting: true,
            connectionError: false,
            connectionRestoreSuccess: false
          })
          break

        case DebugEvents.Reconnecting:
          console.log('NATS attempting reconnect...', status?.data)
          // Optional: could track attempt count here
          break

        case Events.Reconnect:
          console.log('NATS reconnected to:', status)
          this.onConnectionStateChange({
            isConnected: true,
            isReconnecting: false,
            connectionError: false,
            connectionRestoreSuccess: true
          })
          break
        case Events.Error:
          console.error('NATS server error:', status.data)
          break
      }
    }
    // console.log('sorry maxnumber of attepts reached.......')

    // Loop exits only when connection is permanently closed
    // (all auto-reconnect attempts exhausted or manual nc.close())
    // console.warn('NATS status monitor ended — connection permanently closed')
    // this._onConnectionStateChange({
    //   isConnected: false,
    //   isReconnecting: false,
    //   connectionError: true,
    // })
  }
 
  async getConnection(serverURL, options = {}) {
    const { maxRetries = 3, timeoutMs = 10000, isManualorInitiaConnection } = options
    // first check if it is a manual or initial connection
    // console.log('get Connection is called -->',this, isManualorInitiaConnection)
    if (isManualorInitiaConnection) {
      this.retryLimitReached = false
      this.disposed = false
    }

    if (this.retryLimitReached) {
      // console.log('maximum number of connection attempts reached')
      throw new Error('Maximum number of connection attempts reached')
    }

    // Return existing valid connection for same server
    // if (this.connection && !this.connection.isClosed() && this.serverURL === serverURL) {
    if (this.connection && !this.connection.isClosed()) {
      // console.log('connection exists', this.serverURL)
      // console.log('check old connection this.connection -->', this.connection, isManualorInitiaConnection)
      this.onConnectionStateChange({ isConnecting: false, isConnected: true, connectionError: false, maxFailedAttempts: false })
      return this.connection
    }

    // If already connecting, wait for that promise instead of recursive call
    if (this.isConnecting && this.connectionPromise) {
      try {
        return await this.connectionPromise
      } catch (error) {
        // If the pending connection failed, we'll try again below
        console.warn('Pending connection failed, retrying...', error)
      }
    }

    // Start new connection attempt
    this.isConnecting = true
    this.onConnectionStateChange({ isConnecting: true })
    this.connectionPromise = this.createConnection(this.isValidUrl(serverURL), maxRetries, timeoutMs)

    try {
      const connection = await this.connectionPromise
      // if there is connecting we will mount an iterator 
      if (connection) {
        this.monitorNats()
      }
      // console.log(Events)
      // console.log(DebugEvents)
      // console.log('connection found -->', connection.status())
      this.onConnectionStateChange({ isConnecting: false, isConnected: true, connectionError: false, maxFailedAttempts: false })
      return connection
    } finally {
      // console.log('finally block is called -->', this)
      this.isConnecting = false
      this.connectionPromise = null
    }
  }

  isValidUrl(url) {
    if (url || url?.trim()) return url
    return this.serverURL
  }

  async createConnection(serverURL, maxRetries, timeoutMs) {
    let lastError = null

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      // Risk 2 fix: stop if disposed before each attempt
      if (this.disposed) {
        // console.log('Connection attempt cancelled - service disposed')
        return null
      }

      try {
        // Close existing connection if switching servers
        if (this.connection && !this.connection.isClosed()) {
          await this.connection.close()
        }

        // Risk 3 fix: track timeout and clean up dangling connect()
        let timeoutId
        let timedOut = false

        const timeoutPromise = new Promise((_, reject) => {
          timeoutId = setTimeout(() => {
            timedOut = true
            reject(new Error(`Connection timeout after ${timeoutMs}ms`))
          }, timeoutMs)
        })

        const connectionPromise = connect({ servers: [this.isValidUrl(serverURL)], maxReconnectAttempts: 10 }).then((conn) => {
          // If timeout already won the race or service was disposed, close the dangling connection
          if (timedOut || this.disposed) {
            // console.log('see connection is closed-->', this.disposed)
            conn.close().catch(() => {})
            return null
          }
          return conn
        })

        const result = await Promise.race([connectionPromise, timeoutPromise])
        clearTimeout(timeoutId)

        if (!result) {
          // Disposed during connect or dangling connection was cleaned up
          return null
        }

        this.connection = result
        // this.serverURL = serverURL

        // Handle unexpected disconnection
        this.connection.closed().then((err) => {
          if (this.disposed) return
          if (this.isReconnecting) {
            this.onConnectionStateChange({
              isConnected: false,
              isReconnecting: false,
              connectionError: false,
              connectionRestoreSuccess: false,
              connectionErrorWhileRetry: true
            })
            // console.log('connection getting closed')
          }
          if (err) {
            console.error('NATS connection closed with error:', err)
          }
          this.connection = null
          this.serverURL = null
        })

        return this.connection
      } catch (error) {
        lastError = error
        console.error(`NATS connection attempt ${attempt}/${maxRetries} failed:`, error.message)

        // Don't retry on last attempt
        if (attempt < maxRetries) {
          // Exponential backoff: 500ms, 1000ms, 2000ms...
          const backoffMs = Math.min(500 * Math.pow(2, attempt - 1), 5000)
          await new Promise(resolve => setTimeout(resolve, backoffMs))

          // Risk 2 fix: check after backoff wait
          if (this.disposed) {
            console.log('Connection attempt cancelled during backoff - service disposed')
            return null
          }
        } else {
          this.onConnectionStateChange({ isConnecting: false, isConnected: false, connectionError: true, maxFailedAttempts: true })
          this.retryLimitReached = true
        }
      }
    }

    // All retries failed
    this.connection = null
    this.serverURL = null
    throw new Error(`Failed to connect to NATS server after ${maxRetries} attempts: ${lastError?.message}`)
  }

  // ... rest of the methods remain the same

  isConnected() {
    return this.connection && !this.connection.isClosed()
  }

  /**
   * Close the NATS connection and reset state
   *
   * @returns {Promise<void>}
   */
  async dispose() {
    // console.log('connnection is desponsed.....')
    this.disposed = true
    try {
      if (this.connection && !this.connection.isClosed()) {
        await this.connection.close()
        // console.log('NATS connection closed')
      }
    } catch (error) {
      console.error('Error closing NATS connection:', error)
    } finally {
      this.connection = null
      this.serverURL = null
      this.isConnecting = false
      this.connectionPromise = null
      this.retryLimitReached = false
    }
  }

  /**
   * Upload reference image to NATS object store
   *
   * @param serverURL
   * @param bucket
   * @param file
   * @param maxRetries
   */
  async uploadRefImg(serverURL, bucket, file, maxRetries = 1) {
    let lastError = null
    // let intialChunkSize = 128
    const reducedChunkSize = [64, 32]
    setBusy()
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // actual work
        const nc = await this.getConnection(serverURL)
        // check is connection is there
        if (!nc || nc.isClosed()) throw new Error('NATS Closed')

        const js = nc.jetstream()
        const os = await js.views.os(bucket)
  
        const fileStream = file.stream()
     
        const meta = {
          name: file.name,
          mimeType: file.type,
        }

        if (attempt > 1) {
          // intialChunkSize = reducedChunkSize[attempt+1]
          const chunkSize = reducedChunkSize[attempt - 2] < 32 ? 32 : reducedChunkSize[attempt - 2]
          
          meta.options = {
            max_chunk_size: parseInt(chunkSize) * 1024
          }
        }
        // console.log(`Chunk size - mta --> `, meta)
        const info = await os.put(meta, fileStream)
        // console.log('info-->', info)
        if (info) {
          clearBusy()
          return info
        }

        throw new Error('error while uploading image')
      } catch (err) {
        lastError = err
        console.log(`error while uploading image ${attempt}/${maxRetries} failed:`, err.message)

        // back of promise for exponentialDelay
        if (attempt < maxRetries) {
          const backoffMs = Math.min(500 * Math.pow(2, attempt - 1), 4000)
          await new Promise(resolve => setTimeout(resolve, backoffMs))
        }
      }
    }
    clearBusy()
    //  finally throwing the error if any
    throw lastError
    
    // try {
    //   setBusy()
    //   const nc = await this.getConnection(serverURL)
    //   const js = nc.jetstream()
    //   const os = await js.views.os(bucket)

    //   const fileStream = file.stream()
    //   const meta = {
    //     name: file.name,
    //     mimeType: file.type,
    //   }
    //   const info = await os.put(meta, fileStream)

    //   clearBusy()
    //   return info
    // } catch (error) {
    //   console.error('Error during upload inside nats store. Probably need to start nats-server on your local machine for this operation.', error)
    //   clearBusy()
    //   return null
    // }
  }









  /**
   * Get image from NATS object store
   *
   * @param serverURL
   * @param bucket
   * @param fileName
   */
  async getImgFromNats(serverURL, bucket, fileName) {
    try {
      const nc = await this.getConnection(serverURL)
      const js = nc.jetstream()
      const os = await js.views.os(bucket)

      const data = await os.getBlob(fileName)
      const blob = new Blob([data], { type: 'image/jpg' })
      // const urlData = URL.createObjectURL(blob)
      // return urlData
      return blob
    } catch (error) {
      console.error('Error while fetching images from Nats-server. Probably need to start nats-server on your local machine for this operation.', error)
      return null
    }
  }

  /**
   * Delete a single object from a NATS JetStream object store by key (same key used for getBlob / put).
   * @param {{ silent?: boolean }} [opts] — when silent, do not call setBusy/clearBusy (for batch work under an outer busy state)
   */
  async deleteObjectFromNats(serverURL, bucket, objectName, opts = {}) {
    const { silent = false } = opts
    if (!objectName || typeof objectName !== 'string') {
      return
    }
    const key = objectName.trim()
    if (!key) {
      return
    }
    if (!silent) {
      setBusy()
    }
    try {
      const nc = await this.getConnection(serverURL)
      if (!nc) {
        return
      }
      const js = nc.jetstream()
      const os = await js.views.os(bucket)
      await os.delete(key)
    } catch (error) {
      console.warn(`NATS object store delete failed [${bucket}] ${key}:`, error?.message || error)
    } finally {
      if (!silent) {
        clearBusy()
      }
    }
  }

  /**
   * Get NATS URLs for array of items with imgReference
   *
   * @param arr
   * @param LOCAL_NATS_SERVER
   * @param LOCAL_AI_REF_IMG
   */
  async getNatsURL(arr, LOCAL_NATS_SERVER, LOCAL_AI_REF_IMG) {
    const res = []
    for (const data of arr) {
      if (data?.imgReference?.includes('nats')) {
        data.initialImgReferenceNats = data.imgReference.includes('nats') ? data.imgReference : ''
        const blob = await this.getImgFromNats(LOCAL_NATS_SERVER, LOCAL_AI_REF_IMG, data?.imgReference)
        data.imgReference = blob ? URL.createObjectURL(blob) : data?.imgReference
        res.push(data)
      } else {
        res.push(data)
      }
    }
    return res
  }

  /**
   * Get NATS URLs for reference images and asset images
   *
   * @param arr
   * @param LOCAL_NATS_SERVER
   * @param LOCAL_AI_REF_IMG
   */
  async getNatsURLForRefAndImgs(arr, LOCAL_NATS_SERVER, LOCAL_AI_REF_IMG) {
    const res = []
    for (const data of arr) {
      if (data.currentImageURL[0] && data.currentImageURL[0].includes('nats')) {
        data.initialCurrentImageURL = data.currentImageURL[0].includes('nats') ? data.currentImageURL[0] : ''
        const refBlob = await this.getImgFromNats(LOCAL_NATS_SERVER, LOCAL_AI_REF_IMG, data.currentImageURL[0])
        data.currentImageURL[0] = refBlob ? URL.createObjectURL(refBlob) : data.currentImageURL[0]
        const convertedImgs = []
        for (const img of data?.images) {
          const imgBlob = await this.getImgFromNats(LOCAL_NATS_SERVER, LOCAL_AI_ASSET_IMG, img)
          const tempImg = imgBlob ? URL.createObjectURL(imgBlob) : img
          convertedImgs.push(tempImg)
        }
        data.images = convertedImgs
        res.push(data)
      } else {
        res.push(data)
      }
    }
    return res
  }

  /**
   * Publish image data to NATS JetStream
   *
   * @param serverURL
   * @param payload
   */

  async publishImgData(serverURL, payload) {
    const sc = StringCodec()
    try {
      // 1. Connect
      const nc = await this.getConnection(serverURL)
  
      // 2. Create JetStream context
      const jsm = await nc.jetstreamManager()
      const js = nc.jetstream()
      const subjectName = constants.subjectName
      const streamName = constants.streamName
      const consumerName = 'images'
  
      // Generate unique messageId
      const msgId = nuid.next()
  
      // get webhook details
      const webhookDetails = await this.getWebhookData()
      
      const payloadWithId = {
        ...payload,
        messageId: msgId,
        webhook: webhookDetails
      }
  
      // create stream only if it doesnt exist
      try {
        // check if stream exists
        await jsm.streams.info(streamName)
      } catch {
        // create if it does not exist
        await jsm.streams.add({
          name: streamName,
          subjects: [subjectName],
        })
      }
  
      // 3. Publish payload data to the subject
      await js.publish(subjectName, sc.encode(JSON.stringify(payloadWithId)))
  
      // // create consumer only if doesn't exists
      // try {
      //   // check if consumer exists
      //   await jsm.consumers.info(streamName, consumerName)
      // } catch {
      //   // create if it does not exist
      //   await jsm.consumers.add(streamName, {
      //     durable_name: consumerName,
      //     filter_subject: subjectName,
      //     ack_policy: AckPolicy.Explicit,
      //     deliver_policy: DeliverPolicy.All,
      //   })
      // }
  
      // // fetch the consumer
      // const consumer1 = await js.consumers.get(streamName, consumerName)
  
      // const m = await consumer1.next() // consume the message
      // const publishedData = JSON.parse(sc.decode(m.data))
      // m.ack()
      return { payloadWithId, msgId }
    } catch (err) {
      console.error('JetStream error:', err)
      throw err
    }
  }

  /**
   * Get webhook data from Firestore
   */
  async getWebhookData() {
    const webHookDetails = await getDoc(doc(firestore, `${GDE_SETTINGS}/${WEBHOOK}`))
    return webHookDetails?.data()
  }
}

// Export singleton instance
// export const natsService = new NatsService()

// Export class for testing purposes
export { NatsService }
